import {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import Eyebrow from '@/components/Common/Eyebrow';
import {borderRadius, colors, fontSize, fontWeight, gradients, lineHeight, spacing, transition} from '@/styles/tokens';

export interface StatCalloutProps {
    /** Optional uppercase kicker, e.g. "INDUSTRY INSIGHT". */
    eyebrow?: string;
    /** The emphasised figure, e.g. "70–80%". Rendered inline, ahead of `text`. */
    stat: string;
    /** Supporting copy that runs on from the stat in the same paragraph. */
    text: string;
    /** Extra sx merged on top of the container styling (spacing overrides, mostly). */
    sx?: SxProps<Theme>;
}

const wrapSx = {
    bgcolor: colors.background.tag,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderLeftWidth: spacing[4],
    borderLeftStyle: 'solid' as const,
    borderLeftColor: colors.primary.main,
    borderRadius: borderRadius.lg,
    px: spacing[24],
    py: spacing[20],
    transition: `transform ${transition.medium}, background ${transition.medium}, border-color ${transition.medium}`,
    '&:hover': {
        transform: 'scale(1.02)',
        background: gradients.darkSection,
        borderLeftColor: 'transparent',
        '& .MuiTypography-root, & .StatCallout-stat': {color: colors.text.inverse}
    }
} as const;

const eyebrowSx = {mb: spacing[8], color: colors.primary.main} as const;

// The stat and its supporting copy share one <p> so they wrap as a single
// paragraph — the stat is a bigger inline <strong>, not a separate block.
const textSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const statSx = {
    fontSize: {xs: fontSize[24], md: fontSize[28]},
    fontWeight: fontWeight.extrabold,
    color: colors.text.primary,
    mr: spacing[8]
} as const;

/**
 * StatCallout (Atom) — a tinted, blue-left-bordered box holding one emphasised
 * figure plus the sentence that qualifies it. Page-agnostic: any section that
 * needs to foreground a single number can use it.
 */
const StatCallout = ({eyebrow, stat, text, sx}: StatCalloutProps) => (
    <Box sx={{...wrapSx, ...sx}}>
        {eyebrow && <Eyebrow text={eyebrow} sx={eyebrowSx} />}
        <Typography className="font-regular-text" component="p" sx={textSx}>
            <Box component="strong" className="font-headers StatCallout-stat" sx={statSx}>
                {stat}
            </Box>
            {text}
        </Typography>
    </Box>
);

export default memo(StatCallout);
